import React from 'react';
import { AlertTriangle } from 'lucide-react';
import { TNaerasDashboardPool, TNaerasPanel } from '@/data/types';
import PanelCard, { Kpi } from './PanelCard';
import { COLORS, isNum, mwh, num, signed } from '../utils/format';

interface PoolBridgePanelProps {
  panel: TNaerasPanel<TNaerasDashboardPool> | undefined;
  splitHour: number | null;
  /** Today's whole-day pumping, from the §2.6 panel. */
  pumpedTodayMwh: number | null;
}

type Kind = 'level' | 'outflow' | 'pump';

interface Column {
  label: string[];
  kind: Kind;
  estimate: boolean;
  /** Bottom and top of the drawn bar, in MWh; null = nothing to draw. */
  from: number | null;
  to: number | null;
  /** The figure printed on the bar, already signed for steps. */
  value: number | null;
  /** Energy an outflow wanted but the pool didn't have. */
  cut?: number;
  /** Why a level column was zeroed, one short line per entry. */
  note?: string[];
}

const FILL: Record<Kind, [string, string]> = {
  level: [COLORS.hybrid, COLORS.hybridEst],
  outflow: [COLORS.neutral, COLORS.neutralEst],
  pump: [COLORS.hydro, COLORS.hydroEst],
};

const RESET_LEVEL = ['μηδενίστηκε:', 'στάθμη < 3,5 m'];
const RESET_POOL = ['μηδενίστηκε:', 'pool ≤ 5 MWh'];

/**
 * An outflow step: it runs down from the level to `afterDispatch`, the pool
 * after the outflow and before pumping, never below 0. When the outflow is
 * bigger than the pool the bar stops at 0 and the rest is reported as cut.
 */
function outflowStep(
  label: string[],
  level: number | null,
  outflow: number | null,
  afterDispatch: number | null,
  estimate: boolean
): Column {
  if (!isNum(level) || !isNum(outflow)) {
    return { label, kind: 'outflow', estimate, from: null, to: null, value: isNum(outflow) ? -outflow : null };
  }

  const floor = isNum(afterDispatch) ? afterDispatch : Math.max(0, level - outflow);
  const cut = outflow - level;

  return {
    label,
    kind: 'outflow',
    estimate,
    from: floor,
    to: level,
    value: -outflow,
    cut: floor === 0 && cut > 0 ? cut : undefined,
  };
}

function pumpStep(label: string[], start: number | null, pumped: number | null, estimate: boolean): Column {
  if (!isNum(start) || !isNum(pumped)) {
    return { label, kind: 'pump', estimate, from: null, to: null, value: pumped };
  }

  return { label, kind: 'pump', estimate, from: start, to: start + pumped, value: pumped };
}

const levelColumn = (label: string[], value: number | null, estimate: boolean, note?: string[]): Column => ({
  label,
  kind: 'level',
  estimate,
  from: isNum(value) ? 0 : null,
  to: value,
  value,
  note,
});

/**
 * The eight columns of the bridge. Columns 1, 4 and 8 come straight from their
 * fields and are never summed from the steps: a reset, or an outflow bigger
 * than the pool, means the steps don't close — and that is correct.
 */
function buildColumns(pool: TNaerasDashboardPool, splitHour: number | null): Column[] {
  const split = splitHour !== null ? String(splitHour).padStart(2, '0') : '—';
  const yesterdayFloor = outflowStep(
    ['Εκροή'],
    pool.yesterday_00_mwh,
    pool.outflow_mwh,
    pool.yesterday_after_dispatch_mwh,
    false
  );
  const todayFloor = outflowStep(
    ['Εκτιμώμενη', 'εκροή'],
    pool.today_00_mwh,
    pool.est_outflow_mwh,
    pool.today_after_dispatch_mwh,
    true
  );
  const pumpedSoFar = pumpStep([`Άντληση`, `00–${split}`], todayFloor.from, pool.pumped_00_split_mwh, false);

  const tomorrowNote = pool.resets.today_level_below_min
    ? RESET_LEVEL
    : pool.resets.today_pool_at_most_min
    ? RESET_POOL
    : undefined;

  return [
    levelColumn(['Pool', 'χθες 00:00'], pool.yesterday_00_mwh, false),
    yesterdayFloor,
    pumpStep(['Άντληση'], yesterdayFloor.from, pool.pumped_mwh, false),
    levelColumn(
      ['Pool', 'σήμερα 00:00'],
      pool.today_00_mwh,
      false,
      pool.resets.yesterday_level_below_min ? RESET_LEVEL : undefined
    ),
    todayFloor,
    pumpedSoFar,
    pumpStep([`Εκτ. άντλησης`, `${split}–24`], pumpedSoFar.to, pool.est_pumped_split_24_mwh, true),
    levelColumn(['Pool', 'αύριο 00:00'], pool.tomorrow_00_mwh, true, tomorrowNote),
  ];
}

const CHART_H = 220;
/** Room above the plot for the top tick and the value labels. */
const TOP = 18;
const BAR_W = 52;
const GAP = 26;
const LEFT = 36;
const WIDTH = LEFT + 8 * BAR_W + 7 * GAP + 10;

const Waterfall: React.FC<{ columns: Column[] }> = ({ columns }) => {
  const top = Math.max(1, ...columns.map((c) => (isNum(c.to) ? c.to : 0)));
  // Four gridlines on a round step, with headroom for the value labels
  const step = [1, 2, 2.5, 5, 10, 20, 25, 50, 100].find((s) => s * 4 >= top * 1.1) ?? Math.ceil(top / 4);
  const max = step * 4;
  const y = (v: number) => TOP + CHART_H - (v / max) * CHART_H;
  const ticks = [0, 1, 2, 3, 4].map((i) => i * step);

  return (
    <div className="w-full overflow-x-auto">
      <svg viewBox={`0 0 ${WIDTH} ${TOP + CHART_H + 70}`} className="h-auto w-full min-w-[620px] overflow-visible">
        {ticks.map((tick) => (
          <g key={tick}>
            <line x1={LEFT} x2={WIDTH} y1={y(tick)} y2={y(tick)} stroke="#f1f5f9" strokeDasharray="3 3" />
            <text x={LEFT - 6} y={y(tick) + 3} textAnchor="end" fontSize="9" fill="#94a3b8" fontWeight="600">
              {num(tick, step % 1 ? 1 : 0)}
            </text>
          </g>
        ))}

        {/* Divider between the measured day and the estimated one */}
        <line
          x1={LEFT + 4 * (BAR_W + GAP) - GAP / 2}
          x2={LEFT + 4 * (BAR_W + GAP) - GAP / 2}
          y1={TOP}
          y2={TOP + CHART_H}
          stroke="#cbd5e1"
          strokeDasharray="4 3"
        />

        {columns.map((column, i) => {
          const x = LEFT + i * (BAR_W + GAP);
          const [solid, light] = FILL[column.kind];
          const hasBar = isNum(column.from) && isNum(column.to);
          const barTop = hasBar ? y(Math.max(column.from!, column.to!)) : y(0);
          const barH = hasBar ? Math.max(2, Math.abs(y(column.from!) - y(column.to!))) : 0;
          const valueText = !isNum(column.value)
            ? '—'
            : column.kind === 'level'
            ? num(column.value)
            : column.kind === 'outflow'
            ? `−${num(Math.abs(column.value))}`
            : signed(column.value);

          return (
            <g key={i}>
              {hasBar && (
                <rect
                  x={x}
                  y={barTop}
                  width={BAR_W}
                  height={barH}
                  rx={6}
                  fill={column.estimate ? light : solid}
                  stroke={solid}
                  strokeWidth={1.5}
                  strokeDasharray={column.estimate ? '4 3' : undefined}
                />
              )}
              <text
                x={x + BAR_W / 2}
                y={barTop - 6}
                textAnchor="middle"
                fontSize="10"
                fontWeight="800"
                fill="#1e293b"
              >
                {valueText}
              </text>
              {column.cut !== undefined && (
                <text x={x + BAR_W / 2} y={y(0) + 42} textAnchor="middle" fontSize="8.5" fontWeight="700" fill="#b45309">
                  κόπηκαν {num(column.cut)}
                </text>
              )}
              {column.label.map((line, l) => (
                <text
                  key={line}
                  x={x + BAR_W / 2}
                  y={y(0) + 16 + l * 11}
                  textAnchor="middle"
                  fontSize="9"
                  fontWeight="700"
                  fill="#64748b"
                >
                  {line}
                </text>
              ))}
              {column.note?.map((line, l) => (
                <text
                  key={line}
                  x={x + BAR_W / 2}
                  y={y(0) + 42 + l * 10}
                  textAnchor="middle"
                  fontSize="8"
                  fontWeight="700"
                  fill="#b45309"
                >
                  {line}
                </text>
              ))}
            </g>
          );
        })}
      </svg>
    </div>
  );
};

/**
 * §2.5 — how the Hybrid Pool went from yesterday to today (measured, columns
 * 1–4) and how it is estimated to go from today to tomorrow (columns 5–8).
 */
export const PoolBridgePanel: React.FC<PoolBridgePanelProps> = ({ panel, splitHour, pumpedTodayMwh }) => (
  <PanelCard
    title="Hybrid Pool · Σήμερα & Αύριο"
    subtitle="Γέφυρα δύο ημερών, σε MWh"
    panel={panel}
    delay={0.4}
  >
    {(pool) => {
      const diff = (a: number | null, b: number | null) => (isNum(a) && isNum(b) ? a - b : null);
      const todayDelta = diff(pool.today_00_mwh, pool.yesterday_00_mwh);
      const tomorrowDelta = diff(pool.tomorrow_00_mwh, pool.today_00_mwh);

      return (
        <>
          {pool.pool_source === 'manual' && (
            <div className="mb-4 flex items-center gap-2 rounded-2xl border border-indigo-200 bg-indigo-50 p-3 text-xs text-indigo-900">
              <AlertTriangle className="h-4 w-4 shrink-0 text-indigo-600" />
              Το σημερινό απόθεμα ορίστηκε χειροκίνητα — η εκτίμηση για αύριο βασίζεται σε αυτό.
            </div>
          )}

          {(pool.pool_source === 'stale' || pool.pool_source === 'seed') && (
            <div className="mb-4 flex items-center gap-2 rounded-2xl border border-amber-200 bg-amber-50 p-3 text-xs text-amber-900">
              <AlertTriangle className="h-4 w-4 shrink-0 text-amber-600" />
              {pool.pool_source === 'stale'
                ? 'Η εκτίμηση βασίζεται σε απόθεμα που δεν έχει εκκαθαριστεί για σήμερα.'
                : 'Η εκτίμηση βασίζεται σε αρχική τιμή αποθέματος — δεν έχει γίνει εκκαθάριση.'}
            </div>
          )}

          <div className="mb-6 grid grid-cols-2 gap-3 xl:grid-cols-4">
            <Kpi
              label="Σήμερα 00:00"
              value={mwh(pool.today_00_mwh)}
              sub={isNum(todayDelta) ? `${signed(todayDelta)} από χθες` : undefined}
              tone="hybrid"
            />
            <Kpi
              label="Αύριο 00:00 εκτ."
              value={mwh(pool.tomorrow_00_mwh)}
              sub={isNum(tomorrowDelta) ? `${signed(tomorrowDelta)} από σήμερα` : undefined}
              tone="hybrid"
            />
            <Kpi label="Εκροή σήμερα" value={mwh(pool.est_outflow_mwh)} />
            <Kpi label="Άντληση σήμερα" value={mwh(pumpedTodayMwh)} tone="hydro" />
          </div>

          <Waterfall columns={buildColumns(pool, splitHour)} />

          <div className="mt-4 flex flex-wrap items-center gap-4 border-t border-slate-100 pt-3 text-[11px] font-semibold text-slate-500">
            <span className="flex items-center gap-1.5">
              <span className="h-2 w-2 rounded-full" style={{ backgroundColor: COLORS.hybrid }} />
              Hybrid Pool
            </span>
            <span className="flex items-center gap-1.5">
              <span className="h-2 w-2 rounded-full" style={{ backgroundColor: COLORS.neutral }} />
              Εκροή
            </span>
            <span className="flex items-center gap-1.5">
              <span className="h-2 w-2 rounded-full" style={{ backgroundColor: COLORS.hydro }} />
              Άντληση
            </span>
            <span className="text-slate-400">Διακεκομμένο περίγραμμα: εκτίμηση</span>
          </div>
        </>
      );
    }}
  </PanelCard>
);

export default PoolBridgePanel;
